import {
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  EnvelopeOpenIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MapPinIcon,
  UserIcon,
  UsersIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";

/** HH:mm–HH:mm in the browser's local zone (API sends UTC ISO; do not substring the T…Z segment). */
export function formatEventLocalTimeRange(startTime, endTime) {
  if (!startTime || !endTime) return "";
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  return `${format(start, "HH:mm")} - ${format(end, "HH:mm")}`;
}
import { nb } from "date-fns/locale";
import {
  CustomerTitle,
  ItemStatus,
  NewLineFormatter,
  ProjectTitle,
} from "../../../components/Utils";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapsButton } from "../../../components/utils/MapsService";
import { PMButton } from "../../../components/utils/PMButton";
import { ReportSimpleAgreementStatus } from "../../../components/dashboard/service/reports/utils";
import { ReportHoursTag } from "../../../components/dashboard/service/reports/report-hours-tag";
import { ItemListProductStatusTag } from "../../../components/dashboard/service/reports/item-list-product-status-tag";

export const eventPath = (event) => {
  switch (event.module) {
    case "report":
      return `/dashboard/service/reports/${event.module_id}`;
    case "absence":
      return `/dashboard/absences/${event.module_id}`;
    case "case":
      return `/dashboard/cases/${event.module_id}`;
    case "order":
      return `/dashboard/orders/${event.module_id}`;
    case "project":
      return `/dashboard/projects/${event.module_id}`;
    case "project_plan":
      return event.project_id
        ? `/dashboard/projects/${event.project_id}/plan`
        : `/dashboard/calendar`;
    case "work_order":
      return `/dashboard/work_orders/${event.module_id}`;
    default:
      return `/dashboard/calendar`;
  }
};

export const getEventIcon = (module) => {
  switch (module) {
    case "report":
      return <WrenchIcon className="h-4 w-4" />;
    case "absence":
      return <ExclamationTriangleIcon className="h-4 w-4" />;
    case "case":
      return <EnvelopeOpenIcon className="h-4 w-4" />;
    case "order":
      return <ClipboardDocumentListIcon className="h-4 w-4" />;
    case "project":
      return <BuildingOffice2Icon className="h-4 w-4" />;
    case "work_order":
      return <ClipboardDocumentListIcon className="h-4 w-4" />;
    default:
      return <CalendarIcon className="h-4 w-4" />;
  }
};

export const getEventColor = (module) => {
  switch (module) {
    case "report":
      return "bg-blue-100 text-blue-800";
    case "absence":
      return "bg-yellow-100 text-yellow-800";
    case "case":
      return "bg-green-100 text-green-800";
    case "order":
      return "bg-purple-100 text-purple-800";
    case "project":
      return "bg-indigo-100 text-indigo-800";
    case "project_plan":
      return "bg-teal-100 text-teal-800";
    case "work_order":
      return "bg-orange-100 text-orange-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const RenderDayView = ({ dayEvents, date, locale = nb, onEventClick }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {/* <div className="text-lg font-medium text-gray-900">
        {format(new Date(date), "EEEE, d. MMMM yyyy", {
          locale: locale,
        })}
      </div> */}
      {dayEvents.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No events scheduled for this day
        </div>
      ) : (
        <div className="space-y-3">
          {dayEvents.map((event) => {
            // Finn riktig schedule for denne dagen hvis working_hours finnes
            let workingDayInfo = null;
            if (
              event.working_hours &&
              typeof event.working_hours === "object" &&
              event.working_hours.schedule &&
              Array.isArray(event.working_hours.schedule) &&
              event.working_day
            ) {
              workingDayInfo = event.working_hours.schedule.find(
                (s) => s.date === event.working_day
              );
            }
            return (
              <div
                key={event.id}
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                onClick={() => {
                  if (event.module === "project_plan" && onEventClick) {
                    onEventClick(event);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && event.module === "project_plan" && onEventClick) {
                    onEventClick(event);
                  }
                }}
                role={event.module === "project_plan" ? "button" : undefined}
                tabIndex={event.module === "project_plan" ? 0 : undefined}
              >
                <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-3">
                  <Link to={eventPath(event)}>
                    <div
                      className={`p-2 rounded-lg ${getEventColor(
                        event.module
                      )}`}
                    >
                      {getEventIcon(event.module)}
                    </div>
                  </Link>

                  <div className="flex-1 min-w-0">
                    <div className="mb-1">
                      <span className="inline-flex items-center text-sm font-medium text-gray-600">
                        {t(event.module)}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                      <h3 className="text-sm font-medium text-gray-900 break-words">
                        {event?.module_details?.displayTitle || event.title}
                      </h3>
                      {event?.module === "project_plan" &&
                        event?.module_details?.project_activity && (
                          <span className="text-xs text-gray-600 break-words">
                            {event.module_details.project_activity.display_title ||
                              event.module_details.project_activity.title}
                          </span>
                        )}
                      {event?.module === "project_plan" &&
                        event?.module_details?.work_order && (
                          <span className="text-xs text-gray-600 break-words">
                            {event.module_details.work_order.displayTitle ||
                              event.module_details.work_order.title}
                          </span>
                        )}
                      <ItemStatus item={event.module_details || event} />
                      {event?.module === "report" && (
                        <ReportSimpleAgreementStatus
                          isApproved={
                            event?.module_details?.appointment_confirmed
                          }
                        />
                      )}
                    </div>

                    <p className="text-sm text-gray-500 mt-1 break-words">
                      <NewLineFormatter
                        value={
                          event?.module_details?.description ||
                          event?.description
                        }
                      />
                    </p>
                    <div className="flex flex-col space-y-2 mt-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
                      {/* Planlagt tid og tidsperiode */}
                      <div className="flex items-center space-x-1 font-semibold text-blue-700">
                        <ClockIcon className="h-4 w-4 flex-shrink-0" />
                        <span className="break-words">
                          {workingDayInfo && !workingDayInfo.day_off
                            ? `${workingDayInfo.start_time} - ${workingDayInfo.end_time} (${workingDayInfo.hours} t)`
                            : event.start_time && event.end_time
                            ? formatEventLocalTimeRange(event.start_time, event.end_time)
                            : event.time || ""}
                        </span>
                      </div>
                      {event.location && (
                        <div className="flex items-center space-x-1">
                          <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                          <span className="break-words">{event.location}</span>
                        </div>
                      )}
                      {event.user && (
                        <div className="flex items-center space-x-1">
                          <UserIcon className="h-4 w-4 flex-shrink-0" />
                          <span className="break-words">
                            {event.user?.displayTitle}
                          </span>
                        </div>
                      )}
                      {event?.module_details?.customer?.id && (
                        <div className="flex items-center space-x-1">
                          <UsersIcon className="h-4 w-4 flex-shrink-0" />
                          <CustomerTitle
                            customer={event?.module_details?.customer}
                          />
                        </div>
                      )}
                      {event?.module_details?.project?.id && (
                        <div className="flex items-center space-x-1">
                          <BuildingOfficeIcon className="h-4 w-4 flex-shrink-0" />
                          <ProjectTitle
                            project={event?.module_details?.project}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-start sm:justify-end w-full sm:w-auto sm:min-w-[300px]">
                    <div className="flex w-full flex-col items-stretch gap-1 sm:items-end">
                      <ReportHoursTag event={event} className="w-full sm:w-auto" />
                      <ItemListProductStatusTag event={event} className="w-full sm:w-auto" />
                    </div>
                    <div className="mt-2 w-full">
                    <MapsButton location={event?.location} />
                    </div>
                    {event.module === "project_plan" && onEventClick ? (
                      <button
                        type="button"
                        onClick={() => onEventClick(event)}
                        className="ml-0 mt-2 inline-flex min-w-full items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 sm:max-w-full"
                      >
                        <EyeIcon className="h-4 w-4 text-left" />
                        <span className="sr-only">{t("calendar.plan_event.title")}</span>
                      </button>
                    ) : (
                      <PMButton
                        to={eventPath(event)}
                        className="ml-0 mt-2 min-w-full sm:max-w-fulltext-left "
                      >
                        <span className="sr-only">
                          {t("View")} {t(event.module)}
                        </span>
                        <EyeIcon className="h-4 w-4 text-left" />
                      </PMButton>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
